import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Empty,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AlertOutlined,
  BookOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useAuth } from "contexts/AuthContext";
import * as pmService from "services/pmService";
import { OLIVEEB_COLORS } from "workspaces/colors";
import paths from "nav/paths";

const { Text } = Typography;

const GUIDE_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"];
const INCIDENT_STATUSES = ["OPEN", "INVESTIGATING", "MITIGATED", "RESOLVED", "CLOSED"];
const INCIDENT_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const TICKET_STATUSES = ["OPEN", "IN_PROGRESS", "BLOCKED", "RESOLVED", "CLOSED", "CANCELLED"];
const TICKET_PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

const pageItems = (page) => (Array.isArray(page?.content) ? page.content : []);

const labelize = (value = "") =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const optionList = (items) => items.map((item) => ({ value: item, label: labelize(item) }));

const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : "Not set");

const recordNumber = (record = {}) => record.number || record.key || "Pending";

const textWrapStyle = {
  display: "block",
  maxWidth: "100%",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
};

const selectStyle = { width: "min(100%, 170px)" };
const projectSelectStyle = { width: "min(100%, 240px)" };

const statusColor = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (["ACTIVE", "RESOLVED", "CLOSED"].includes(normalized)) return "green";
  if (["CRITICAL", "URGENT", "BLOCKED"].includes(normalized)) return "red";
  if (["HIGH", "INVESTIGATING", "IN_PROGRESS"].includes(normalized)) return "orange";
  if (["ARCHIVED", "CANCELLED"].includes(normalized)) return "default";
  return "blue";
};

const tag = (value) =>
  value ? (
    <Tag color={statusColor(value)} style={{ marginInlineEnd: 0, maxWidth: "100%", whiteSpace: "normal" }}>
      {labelize(value)}
    </Tag>
  ) : (
    <Text type="secondary">Not set</Text>
  );

const textCell = (value) =>
  value ? <Text style={textWrapStyle}>{value}</Text> : <Text type="secondary">Not set</Text>;

const formatText = (value, { maxLength = 60, strong = false } = {}) => {
  const text = String(value ?? "");
  const isTruncated = text.length > maxLength;
  const displayText = isTruncated ? `${text.slice(0, maxLength).trimEnd()}...` : text;
  const content = (
    <Text strong={strong} style={textWrapStyle}>
      {displayText}
    </Text>
  );

  return isTruncated ? <Tooltip title={text}>{content}</Tooltip> : content;
};

const pageConfig = {
  guides: {
    title: "Troubleshooting Guides",
    singular: "guide",
    icon: <BookOutlined />,
    subtitle: "Build and maintain procedures for diagnosing system failures",
    list: pmService.listTroubleshootingGuides,
    remove: pmService.deleteTroubleshootingGuide,
    route: paths.troubleshootingGuides,
    statuses: GUIDE_STATUSES,
    buildParams: (filters) => ({ status: filters.status || undefined }),
  },
  incidents: {
    title: "Incidents",
    singular: "incident",
    icon: <AlertOutlined />,
    subtitle: "Capture, track, and resolve system failures and operational events",
    list: pmService.listIncidents,
    remove: pmService.deleteIncident,
    route: paths.incidents,
    statuses: INCIDENT_STATUSES,
    buildParams: (filters) => ({
      status: filters.status || undefined,
      severity: filters.severity || undefined,
      projectId: filters.projectId || undefined,
    }),
  },
  tickets: {
    title: "Tickets",
    singular: "ticket",
    icon: <TagsOutlined />,
    subtitle: "Create and manage support, incident, change, and task tickets",
    list: pmService.listTickets,
    remove: pmService.deleteTicket,
    route: paths.tickets,
    statuses: TICKET_STATUSES,
    buildParams: (filters) => ({
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      projectId: filters.projectId || undefined,
    }),
  },
};

const Operations = ({ resource = "guides" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const config = pageConfig[resource] || pageConfig.guides;
  const [records, setRecords] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const actorId = user?.id || user?.userId || user?.uid || "";

  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name || project.code || project.id,
  }));

  const fetchProjects = useCallback(async () => {
    if (resource === "guides") return;
    try {
      const page = await pmService.listProjects({ page: 0, size: 100, sort: "name,asc" });
      setProjects(pageItems(page));
    } catch {
      setProjects([]);
    }
  }, [resource]);

  const fetchRecords = useCallback(
    async (nextPagination = pagination) => {
      setLoading(true);
      try {
        const page = await config.list({
          page: nextPagination.current - 1,
          size: nextPagination.pageSize,
          sort: "createdAt,desc",
          ...config.buildParams(filters),
        });
        setRecords(pageItems(page));
        setPagination((prev) => ({
          ...prev,
          current: (page?.number ?? nextPagination.current - 1) + 1,
          pageSize: page?.size || nextPagination.pageSize,
          total: page?.totalElements || 0,
        }));
      } catch (error) {
        message.error(error?.message || `Failed to load ${config.title.toLowerCase()}`);
      } finally {
        setLoading(false);
      }
    },
    [config, filters, pagination],
  );

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchRecords({ current: 1, pageSize: pagination.pageSize, total: pagination.total });
  }, [resource, filters]);

  const handleDelete = async (record) => {
    try {
      await config.remove(record.id, actorId);
      message.success(`${config.title.slice(0, -1)} deleted`);
      fetchRecords();
    } catch (error) {
      message.error(error?.message || `Failed to delete ${config.singular}`);
    }
  };

  const commonColumns = [
    {
      title: "Number",
      dataIndex: "number",
      width: 120,
      responsive: ["sm"],
      render: (_, record) => <Text strong>{recordNumber(record)}</Text>,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (value) => (
        <Space direction="vertical" size={2}>
          {formatText(value, { maxLength: 20, strong: true })}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: tag,
    },
  ];

  const columnsByResource = {
    guides: [
      ...commonColumns,
      {
        title: "System Area",
        dataIndex: "systemArea",
        width: 180,
        responsive: ["md"],
        render: (value) => (value ? formatText(value, { maxLength: 24 }) : <Text type="secondary">Not set</Text>),
      },
      { title: "Updated", dataIndex: "updatedAt", width: 190, responsive: ["lg"], render: formatDate },
    ],
    incidents: [
      ...commonColumns,
      { title: "Severity", dataIndex: "severity", width: 120, responsive: ["sm"], render: tag },
      { title: "Affected System", dataIndex: "affectedSystem", width: 180, responsive: ["md"], render: textCell },
      { title: "Detected", dataIndex: "detectedAt", width: 190, responsive: ["lg"], render: formatDate },
    ],
    tickets: [
      ...commonColumns,
      { title: "Type", dataIndex: "type", width: 130, responsive: ["md"], render: tag },
      { title: "Priority", dataIndex: "priority", width: 120, responsive: ["sm"], render: tag },
      { title: "Requester Email", dataIndex: "requesterName", width: 220, responsive: ["lg"], render: textCell },
      { title: "Due", dataIndex: "dueAt", width: 190, responsive: ["lg"], render: formatDate },
    ],
  };

  const columns = [
    ...columnsByResource[resource],
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_, record) => (
        <Space size={4} onClick={(event) => event.stopPropagation()}>
          <Button
            aria-label="Edit"
            icon={<EditOutlined />}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`${config.route}/${record.id}/edit`);
            }}
          />
          <Popconfirm
            title={`Delete this ${config.singular}?`}
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button
              aria-label="Delete"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(event) => event.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filterControls = useMemo(
    () => (
      <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
        <Select
          allowClear
          placeholder="Status"
          value={filters.status}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          options={optionList(config.statuses)}
          style={selectStyle}
        />
        {resource === "incidents" ? (
          <Select
            allowClear
            placeholder="Severity"
            value={filters.severity}
            onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))}
            options={optionList(INCIDENT_SEVERITIES)}
            style={selectStyle}
          />
        ) : null}
        {resource === "tickets" ? (
          <Select
            allowClear
            placeholder="Priority"
            value={filters.priority}
            onChange={(value) => setFilters((prev) => ({ ...prev, priority: value }))}
            options={optionList(TICKET_PRIORITIES)}
            style={selectStyle}
          />
        ) : null}
        {resource !== "guides" ? (
          <Select
            allowClear
            showSearch
            placeholder="Project"
            value={filters.projectId}
            onChange={(value) => setFilters((prev) => ({ ...prev, projectId: value }))}
            options={projectOptions}
            optionFilterProp="label"
            style={projectSelectStyle}
          />
        ) : null}
        <Button icon={<ReloadOutlined />} onClick={() => fetchRecords()} />
      </Space>
    ),
    [config.statuses, fetchRecords, filters, projectOptions, resource],
  );

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={config.icon}
          title={config.title}
          subtitle={config.subtitle}
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${config.route}/new`)}>
          New {config.singular}
        </Button>
      </div>

      <div
        style={{
          border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
          borderRadius: 8,
          background: OLIVEEB_COLORS.white,
          padding: 16,
          boxShadow: `0 10px 28px ${OLIVEEB_COLORS.shadow}`,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, width: "100%" }}>
          {filterControls}
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={records}
          columns={columns}
          size="middle"
          scroll={{ x: "max-content" }}
          onRow={(record) => ({
            onClick: () => navigate(`${config.route}/${record.id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${config.title.toLowerCase()} found`} />
            ),
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            onChange: (current, pageSize) => {
              const next = { ...pagination, current, pageSize };
              setPagination(next);
              fetchRecords(next);
            },
          }}
        />
      </div>
    </div>
  );
};

export default Operations;
